import React, { useEffect, useMemo, useRef } from "react";
import styles from "./AIBackgroundChanger.module.css";
import { Button, Container, Typography, Box, Grid } from "@mui/material";
import Navbar from "../../Navbar";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Footer from "../../Footer";
import FAQ from "./FAQ";
import PricingCards from "../../PricingCards";
import ImageSlider from "../../ImageSlider";
import ImageSlider2 from "../../ImageSlider2";
import DownloadSection from "../../DownloadSection";
import { Helmet } from "react-helmet"; // Added for SEO management
import { useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const AIBackgroundChanger = () => {
  // Enhanced SEO Meta Content
  const seoData = useMemo(() => ({
    title: "AI Background Changer | Instant Background Replacement Tool | PhotoFix",
    description: "Transform photo backgrounds instantly with AI. Perfect for e-commerce, portraits, and social media. Change to any background with professional precision. Free trial available.",
    keywords: "AI background changer, background replacement, photo background editor, AI photo editing, automatic background change",
    canonical: "https://photofix.com/ai-background-changer",
    schemaJSON: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "AI Background Changer",
      operatingSystem: "Web",
      applicationCategory: "PhotoEditor",
      screenshot: "https://cdn.example.com/screenshot.jpg",
      downloadUrl: "https://photofix.com/download",
      offers: {
        "@type": "Offer",
        price: "9.99",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        url: "https://photofix.com/ai-background-changer",
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        ratingCount: "2847",
      },
      image: "https://cdn.photofix.com/og-image.jpg",
      brand: {
        "@type": "Brand",
        name: "PhotoFix",
      },
    },
  }), []);

  // Refs declarations
  const sectionRefs = useRef([]);
  const gradientRef = useRef(null);
  const headingRef = useRef([]);
  const imageRef = useRef(null);
  const buttonRef = useRef(null);
  const featureRefs = useRef([]);
  const videoRefs = useRef([]);
  const sectionHeadingRef = useRef(null);
  const heroContentRef = useRef(null);
  const descriptionRef = useRef(null); // New ref for description
  const navigate=useNavigate()

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Text animations
      gsap.fromTo(
        headingRef.current,
        {
          opacity: 0,
          y: 50,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          stagger: 0.3,
          ease: "power4.out",
          scrollTrigger: {
            trigger: heroContentRef.current,
            start: "top 80%",
            end: "top 50%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Gradient text specific animation
      gsap.fromTo(
        headingRef.current[0],
        {
          backgroundPosition: "100% 100%",
        },
        {
          backgroundPosition: "0% 100%",
          duration: 2,
          ease: "power2.inOut",
          scrollTrigger: {
            trigger: headingRef.current[0],
            start: "top 80%",
            end: "top 30%",
            scrub: 1,
          },
        }
      );

      // Stroke text animation
      gsap.fromTo(
        headingRef.current[1],
        {
          textStrokeWidth: "0px",
          opacity: 0,
        },
        {
          textStrokeWidth: "3px",
          opacity: 1,
          duration: 1.5,
          ease: "power4.out",
          scrollTrigger: {
            trigger: headingRef.current[1],
            start: "top 85%",
            end: "top 60%",
            scrub: 1,
          },
        }
      );

      // Description animation
      gsap.fromTo(
        descriptionRef.current,
        {
          opacity: 0,
          y: 30,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: descriptionRef.current,
            start: "top 90%",
            end: "top 70%",
            toggleActions: "play none none reverse",
          },
        }
      );
      // Gradient position animation
      gsap.fromTo(
        gradientRef.current,
        { backgroundPosition: "0% 0%" },
        {
          backgroundPosition: "100% 100%",
          ease: "power2.inOut",
          scrollTrigger: {
            start: "top top",
            end: "bottom bottom",
            scrub: 1.5,
          },
        }
      );

      // Color intensity animation
      gsap.fromTo(
        gradientRef.current,
        {
          backgroundImage:
            "linear-gradient(315deg, #0a0c24 0%, #1a1b3a 50%,rgb(23, 5, 24) 100%)",
        },
        {
          backgroundImage:
            "linear-gradient(315deg, #050814 0%, #0f1028 50%, #1a081b 100%)",
          ease: "power2.inOut",
          scrollTrigger: {
            start: "top center",
            end: "bottom center",
            scrub: 1,
          },
        }
      );
      // Hero section animations
      gsap.from(imageRef.current, {
        scale: 1.2,
        duration: 1.5,
        ease: "power4.out",
        transformOrigin: "center center",
      });

      const heroTl = gsap.timeline({
        scrollTrigger: {
          trigger: heroContentRef.current,
          start: "top top",
          end: "+=100%",
          scrub: 1.5,
          pin: true,
          anticipatePin: 1,
        },
      });

      heroTl
        .to(imageRef.current, {
          scale: 1.8,
          y: 200,
          opacity: 0,
          ease: "power3.inOut",
        })
        .to(
          heroContentRef.current,
          {
            y: -200,
            opacity: 0,
            ease: "power3.inOut",
          },
          0
        );

      // Feature section animations
      // Section heading animation
      gsap.fromTo(
        sectionHeadingRef.current,
        { y: 50, opacity: 0, filter: "brightness(1)" },
        {
          y: 0,
          opacity: 1,
          filter: "brightness(1.5)",
          duration: 1.5,
          scrollTrigger: {
            trigger: sectionHeadingRef.current,
            start: "top 90%",
            end: "top 60%",
            scrub: 1.5,
            toggleActions: "play none none reverse",
          },
        }
      );

      // Feature sections animations
      features.forEach((feature, index) => {
        const video = videoRefs.current[index];
        const content = featureRefs.current[index];
        const videoContainer = video.parentElement;
        const videoMask = videoContainer.querySelector(".videoMask");
        const direction = feature.direction === "left" ? -1 : 1;

        // Create timeline for coordinated animations
        const videoTl = gsap.timeline({
          scrollTrigger: {
            trigger: videoContainer,
            start: "top bottom-=10%",
            end: "top center",
            scrub: 1.5,
            toggleActions: "play pause resume reset",
          },
        });

        // Video entrance animation
        videoTl
          .fromTo(
            video,
            { scale: 0.9, opacity: 0 },
            { scale: 1, opacity: 1, duration: 1.2 }
          )
          .fromTo(videoMask, { opacity: 1 }, { opacity: 0, duration: 1.5 }, 0);

        // Content animation
        gsap.fromTo(
          content,
          { x: 150 * direction, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            scrollTrigger: {
              trigger: content,
              start: "top 85%",
              end: "top 50%",
              scrub: 1.2,
            },
          }
        );

        // Reverse mask animation on scroll up
        gsap.fromTo(
          videoMask,
          { opacity: 0 },
          {
            opacity: 1,
            scrollTrigger: {
              trigger: videoContainer,
              start: "center center",
              end: "bottom top",
              scrub: 1,
              toggleActions: "play pause resume reset",
            },
          }
        );

        // Content slide animation
        gsap.fromTo(
          content,
          { x: 150 * direction, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            scrollTrigger: {
              trigger: content,
              start: "top 85%",
              end: "top 50%",
              scrub: 1.2,
              toggleActions: "play none none reverse",
            },
          }
        );

        // Reverse animations on scroll up
        gsap.fromTo(
          video,
          { scale: 1 },
          {
            scale: 0.9,
            opacity: 0,
            scrollTrigger: {
              trigger: video,
              start: "top center",
              end: "bottom top",
              scrub: 1,
              toggleActions: "restart pause resume reset",
            },
          }
        );
      });
    });
    return () => ctx.revert();
  }, []);

  const features = [
    {
      title: "AI Background Generation & Replacement",
      content:
        "Automatically remove backgrounds and generate new environments with AI. Choose from 50+ professional templates (studio, nature, abstract) or create custom backgrounds using text prompts. Perfect for e-commerce product photos and portrait editing.",
      video:
        "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      direction: "left",
    },
    {
      title: "Precision Background Editing Toolkit",
      content:
        "Refine AI-generated backgrounds with pixel-level control. Adjust lighting, match colors to foreground subjects, and blend edges naturally. Expand images beyond original borders for social media-ready compositions.",
      video:
        "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      direction: "right",
    },
    {
      title: "Real-Time Background Preview",
      content:
        "See background changes instantly as you edit. Swap between different AI-generated backgrounds in real-time, test color schemes, and preview how different environments affect your composition. No more manual masking!",
      video:
        "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      direction: "left",
    },
    {
      title: "4K Background Upscaling",
      content:
        "Enhance low-quality backgrounds to 4K resolution without artifacts. Fix blurry backgrounds from old photos, improve AI-generated textures, and prepare images for professional printing. Preserve sharp edges around complex subjects.",
      video:
        "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      direction: "right",
    },
  ];

  return (
    <Box className={styles.mainContainer} ref={gradientRef}>
      {/* Added SEO Head Section */}
      <Helmet>
        <html lang="en" />
        <meta charSet="utf-8" />
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonical} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoData.canonical} />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta
          property="og:image"
          content="https://cdn.photofix.com/og-image.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta
          name="twitter:image"
          content="https://cdn.photofix.com/twitter-card.jpg"
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(seoData.schemaJSON)}
        </script>

        {/* Additional SEO Enhancements */}
        <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
        <meta name="robots" content="index, follow" />
        <link rel="alternate" hreflang="x-default" href={seoData.canonical} />
      </Helmet>
      <Navbar />
      <Container className={styles.contentContainer}>
        <Box className={styles.textContainer}>
          <Typography variant="h1" className={styles.heading}>
            <span
              ref={(el) => (headingRef.current[0] = el)}
              className={styles.gradientText}
            >
              AI-Powered Background Transformation:
            </span>
            <br />
            <span
              ref={(el) => (headingRef.current[1] = el)}
              className={styles.strokeText}
            >
              Remove Backgrounds in Seconds
            </span>
          </Typography>

          <Typography
            variant="subtitle1"
            className={styles.description}
            ref={descriptionRef} // Added ref here
          >
            Professional-grade AI background changer for perfect photo editing.
            Automatically detect subjects, remove backgrounds with precision,
            and replace with custom environments. Ideal for e-commerce, portrait
            photography, and creative projects.
          </Typography>

          <Box className={styles.buttonContainer}>
            <Button
              variant="contained"
              className={styles.generateButton}
              ref={buttonRef}
              onClick={()=>navigate("/dashboard/ai-background-changer")}
            >
              <span className={styles.buttonText}>
                Change Background Now
              </span>
              <div className={styles.buttonGlow} />
            </Button>
          </Box>
        </Box>

        <Box className={styles.imageContainer}>
          <div className={styles.imageWrapper}>
          <img
              ref={imageRef}
              src="https://cdn.web.imagine.art/imagine-frontend/assets/images/ai-image-generator-hero-image.png"
              alt="AI Art Generator Platform Creating Digital Paintings"
              title="AI Art Generator Platform Creating Digital Paintings"
              class={styles.image}
              width="1200"
              height="630"
              loading="lazy"
              decoding="async"
              aria-describedby="image-caption"
            />
            <div className={styles.imageGlow} />
            <div className={styles.imageShine} />
          </div>
        </Box>
      </Container>
      <ImageSlider />
      <ImageSlider2 />
      <Container maxWidth="xl" className={styles.featureContainer}>
        <div ref={sectionHeadingRef}>
          <h2 className={styles.sectionHeading}>
            Core Features for AI-Powered Editing
          </h2>
          <p className={styles.sectionSubheading}>
            Professional toolkit for background removal & replacement
          </p>
        </div>

        {features.map((feature, index) => (
          <section
            key={feature.title}
            ref={(el) => (sectionRefs.current[index] = el)}
            className={styles.featureSection}
          >
            <Grid container spacing={6} alignItems="center">
              <Grid
                item
                xs={12}
                md={6}
                order={{ xs: 2, md: index % 2 ? 2 : 1 }}
              >
                <div
                  ref={(el) => (featureRefs.current[index] = el)}
                  className={`${styles.contentWrapper} feature-content`}
                >
                  <Typography variant="h2" className={styles.featureTitle}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" className={styles.featureText}>
                    {feature.content}
                  </Typography>
                </div>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                order={{ xs: 1, md: index % 2 ? 1 : 2 }}
              >
                <div className={styles.videoContainer}>
                  <video
                    ref={(el) => (videoRefs.current[index] = el)}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className={styles.featureVideo}
                  >
                    <source src={feature.video} type="video/webm" />
                  </video>
                  <div className={styles.videoMask} />
                </div>
              </Grid>
            </Grid>
          </section>
        ))}
      </Container>
      <PricingCards />
      <DownloadSection />
      <FAQ />
      <Footer />
    </Box>
  );
};

export default AIBackgroundChanger;
